import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LANGUAGES } from "@/i18n";
import { visibleSections } from "./navConfig";
import { Brand } from "./Brand";
import { SidebarContent } from "./SidebarContent";
import { NotificationBell } from "./NotificationBell";
import { HalalToggle } from "./HalalToggle";
import { CurrencySelect, RateBanner } from "./CurrencySelect";
import { NavSearch } from "./NavSearch";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Menu, Languages, ChevronDown, Check, Sun, Moon, User, ShieldCheck, LogOut, Contrast,
} from "lucide-react";

const linkClass = ({ isActive }) => `nav-pill ${isActive ? "nav-pill-active" : ""}`;

const GroupMenu = ({ item }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const active = item.children.some((c) => location.pathname === c.to);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={item.testid}
          className={`nav-pill ${active ? "nav-pill-active" : ""}`}>
          <item.icon className="h-4 w-4" /> {item.label}
          <ChevronDown className="nav-caret h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52" data-testid={`${item.testid}-menu`}>
        {item.children.map((c) => (
          <DropdownMenuItem key={c.to} onClick={() => navigate(c.to)} data-testid={c.testid}>
            <c.icon className="h-4 w-4 me-2 text-muted-foreground" />{c.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LanguageMenu = ({ lang, setLang, t }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" className="nav-chip gap-1.5 px-3 text-xs font-semibold" data-testid="top-nav-language-toggle">
        <Languages className="h-4 w-4" />
        <span className="hidden sm:inline">{(LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0]).native}</span>
        <ChevronDown className="nav-caret h-3.5 w-3.5 opacity-70" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56" data-testid="language-menu">
      <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <div className="max-h-[28rem] overflow-auto">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)}
            className="flex items-center justify-between" data-testid={`language-option-${l.code}`}>
            <span className="flex items-center gap-2">
              <span className="font-medium">{l.native}</span>
              <span className="text-xs text-muted-foreground">{l.label}</span>
            </span>
            {lang === l.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const TopNavBar = () => {
  const { user, logout, lang, setLang, contrast, setContrast, theme, toggleTheme, t } = useApp();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(false);
  const items = visibleSections(t, user?.pro_mode).flatMap((s) => s.items);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
        <div className="flex h-24 items-center justify-between gap-2">
          <div className="flex items-center gap-2 shrink-0">
            <Sheet open={sheet} onOpenChange={setSheet}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden rounded-full nav-icon-btn" data-testid="mobile-nav-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-secondary">
                <SidebarContent onNavigate={() => setSheet(false)} />
              </SheetContent>
            </Sheet>
            <NavLink to="/" data-testid="topnav-brand"><Brand /></NavLink>
          </div>

          <nav className="hidden lg:flex items-center gap-1 mx-3 min-w-0 flex-1 justify-center overflow-x-auto no-scrollbar nav-scroll py-1">
            {items.map((it) => (
              it.children ? <GroupMenu key={it.label} item={it} /> : (
                <NavLink key={it.to} to={it.to} end={it.end} className={linkClass} data-testid={it.testid}>
                  <it.icon className="h-4 w-4" /> {it.label}
                </NavLink>
              )
            ))}
          </nav>

          <div className="flex items-center gap-1.5 min-w-0">
            <span data-testid="top-nav-search"><NavSearch /></span>
            <span data-testid="top-nav-currency"><CurrencySelect /></span>
            <span data-testid="top-nav-halal-toggle"><HalalToggle /></span>
            <span data-testid="top-nav-notifications"><NotificationBell /></span>
            <div className="hidden sm:flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle"
                className="rounded-full nav-icon-btn"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
                {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
              </Button>
              <LanguageMenu lang={lang} setLang={setLang} t={t} />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="top-nav-profile-menu"
                  className="rounded-full nav-icon-btn">
                  <div className="h-8 w-8 rounded-full navy-strip text-white flex items-center justify-center text-sm font-semibold ring-1 ring-black/5">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="h-4 w-4 me-2" /> {t("nav_profile")}
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="h-4 w-4 me-2" /> {t("nav_admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setContrast(!contrast)} data-testid="settings-high-contrast-toggle">
                  <Contrast className="h-4 w-4 me-2" /> {t("high_contrast")}: {contrast ? "On" : "Off"}
                </DropdownMenuItem>
                <DropdownMenuItem className="sm:hidden" onClick={toggleTheme} data-testid="menu-theme-toggle">
                  {theme === "dark" ? <Sun className="h-4 w-4 me-2" /> : <Moon className="h-4 w-4 me-2" />}
                  {theme === "dark" ? "Light mode" : "Dark mode"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 me-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <RateBanner />
    </header>
  );
};
